import { ReactNode } from "react";

import {
    isVaultTemporarilyUnavailable,
    LendingVaultExpanded,
    VAULT_CATEGORY_FILTER_TAGS
} from "@bridgesplit/abf-react";
import { TokenListMetadata, VaultMetadataTag } from "@bridgesplit/abf-sdk";
import { ALL_VALUE, AllValueType, DISABLED_APY_BIGINT, formatToken } from "@bridgesplit/utils";
import { Tooltip } from "@bridgesplit/ui";
import { Box } from "@mui/material";
import {
    AppsOutlined,
    LocalAtmOutlined,
    LayersOutlined,
    CurrencyExchangeOutlined,
    CurrencyBitcoinOutlined,
    WhatshotOutlined,
    StarBorderOutlined,
    CheckCircleOutlined
} from "@mui/icons-material";
import { COPY, EARN_SLUG, LEND_VAULT_DETAIL_SLUG, VAULT_MANAGER_SLUG } from "app/constants";

/** Frontend-only filter: "all" shows every vault; specific tags filter by vault metadata. */
export const VAULT_FILTER_ALL = ALL_VALUE;
export type VaultFilterTag = AllValueType | VaultMetadataTag;

export function getLendingVaultPath(lendVault: LendingVaultExpanded | undefined) {
    if (!lendVault || isVaultTemporarilyUnavailable(lendVault)) return "#";
    const identifier = lendVault.vault.vaultIdentifier ?? lendVault.vault.address;
    return `${LEND_VAULT_DETAIL_SLUG}/${identifier}`;
}

/** Greys out its children and adds the unavailable tooltip when the vault is blocked. */
export function VaultUnavailableWrapper({
    vaultExpanded,
    children
}: {
    vaultExpanded: LendingVaultExpanded | undefined;
    children: ReactNode;
}) {
    if (!isVaultTemporarilyUnavailable(vaultExpanded)) return <>{children}</>;
    return (
        <Tooltip fullWidth title={COPY.GENESIS_VAULT_UNAVAILABLE}>
            <Box sx={{ width: "100%", height: "100%", opacity: 0.4, filter: "saturate(0)", cursor: "not-allowed" }}>
                {children}
            </Box>
        </Tooltip>
    );
}

export function getVaultManagerPath(lendVault: LendingVaultExpanded | undefined) {
    if (!lendVault) return "#";
    const identifier = lendVault.vault.vaultIdentifier ?? lendVault.vault.address;
    return `${VAULT_MANAGER_SLUG}/${identifier}`;
}
export function getEarnVaultPath(earnVault: { address: string } | undefined) {
    if (!earnVault) return "#";
    const identifier = earnVault.address;
    return `${EARN_SLUG}/${identifier}`;
}
type VaultFilterMeta = {
    icon: JSX.Element;
    label: string;
    shortLabel: string;
    tag: VaultFilterTag;
    sortPosition: number;
    hideInCategories?: boolean;
};

export const VAULT_FILTER_TAG_ORDER = [
    VAULT_FILTER_ALL,
    ...VAULT_CATEGORY_FILTER_TAGS
] as const satisfies readonly VaultFilterTag[];

type VaultFilterTags = (typeof VAULT_FILTER_TAG_ORDER)[number];

const VAULT_FILTER_METADATA: Record<VaultFilterTags, Omit<VaultFilterMeta, "tag" | "sortPosition">> = {
    [VAULT_FILTER_ALL]: { icon: <AppsOutlined />, label: "All", shortLabel: "All", hideInCategories: true },
    [VaultMetadataTag.Featured]: { icon: <StarBorderOutlined />, label: "Featured", shortLabel: "Featured" },
    [VaultMetadataTag.Popular]: { icon: <WhatshotOutlined />, label: "Popular", shortLabel: "Popular" },
    [VaultMetadataTag.Stablecoins]: { icon: <LocalAtmOutlined />, label: "Stablecoins", shortLabel: "Stables" },
    [VaultMetadataTag.SOL]: { icon: <LayersOutlined />, label: "SOL & LST", shortLabel: "SOL/LST" },
    [VaultMetadataTag.LP]: { icon: <CurrencyExchangeOutlined />, label: "LP", shortLabel: "LPs" },
    [VaultMetadataTag.BTC]: { icon: <CurrencyBitcoinOutlined />, label: "BTC", shortLabel: "BTC" },
    [VaultMetadataTag.Collateral]: {
        icon: <CheckCircleOutlined />,
        label: "Collateral LP",
        shortLabel: "Collateral LP"
    }
};

export function getVaultFilterMetadata(tag: VaultFilterTag | undefined): VaultFilterMeta | undefined {
    if (!tag || !(tag in VAULT_FILTER_METADATA)) return undefined;
    return {
        ...VAULT_FILTER_METADATA[tag as VaultFilterTags],
        tag,
        sortPosition: VAULT_FILTER_TAG_ORDER.indexOf(tag as VaultFilterTags)
    };
}

export default function CapCell({
    value,
    metadata,
    withSymbol = false
}: {
    value: bigint | undefined;
    metadata: TokenListMetadata | undefined;
    withSymbol?: boolean;
}) {
    if (value === DISABLED_APY_BIGINT) return "Disabled";
    if (value === undefined || metadata?.decimals === undefined) return "--";
    return formatToken(
        { amount: value, decimals: metadata.decimals },
        { symbol: withSymbol ? metadata.symbol : undefined }
    );
}
